import React from 'react';

import { ResponsiveBreakpoint, useResponsiveBreakpoint } from '../responsive';

import { Header } from './header';
import Layout from './layout';
import { isNavItemActive } from './nav-active';
import { NavigationMenu } from './navigation-menu';
import type { NavItem, NavSectionData } from './navigation-menu';

export type { NavSectionData, NavItem } from './navigation-menu';
export { NavSection } from './nav-section';
export type { NavSectionProps } from './nav-section';

function findActiveItem(
  sections: NavSectionData[],
  selectedPath: string,
): NavItem | undefined {
  for (const section of sections) {
    for (const item of section.items) {
      if (!item.path) continue;
      const match = isNavItemActive(selectedPath, item.path, item.end);
      if (match) return item;
    }
  }
  return undefined;
}

export interface AppShellProps {
  brandName: string;
  navSections: NavSectionData[];
  selectedPath?: string;
  headerRight?: React.ReactNode;
  workspaceName?: string | null;
  environment?: string;
  webVersion?: string;
  serviceVersion?: string;
  isServiceVersionLoading?: boolean;
  fullBleed?: boolean;
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
  brandName,
  navSections,
  selectedPath,
  headerRight,
  workspaceName,
  environment,
  webVersion,
  serviceVersion,
  isServiceVersionLoading,
  fullBleed = false,
  children,
}) => {
  const breakpoint = useResponsiveBreakpoint();

  const [isOpen, setIsOpen] = React.useState(
    breakpoint >= ResponsiveBreakpoint.XLarge,
  );

  React.useEffect(() => {
    setIsOpen(breakpoint >= ResponsiveBreakpoint.XLarge);
  }, [breakpoint]);

  const activeItem = findActiveItem(navSections, selectedPath ?? '');
  const hasWorkspaceName = workspaceName != null && workspaceName !== '';
  const pageTitle = hasWorkspaceName
    ? workspaceName
    : (activeItem?.label ?? brandName);
  const pageSubtitle = hasWorkspaceName
    ? activeItem?.label
    : activeItem?.description;

  return (
    <Layout fullBleed={fullBleed}>
      <Layout.Nav>
        <NavigationMenu
          isOpen={isOpen}
          brandName={brandName}
          environment={environment}
          webVersion={webVersion}
          serviceVersion={serviceVersion}
          isServiceVersionLoading={isServiceVersionLoading}
          sections={navSections}
          selectedPath={selectedPath}
          onToggle={() => setIsOpen(!isOpen)}
        />
      </Layout.Nav>
      <Layout.Header>
        <Header title={pageTitle} subtitle={pageSubtitle}>
          <Header.Actions>{headerRight}</Header.Actions>
        </Header>
      </Layout.Header>
      <Layout.Main>{children}</Layout.Main>
    </Layout>
  );
};
